import { ChevronRight } from 'lucide-react';
import type { ReactNode, RefObject } from 'react';
import { Link } from 'react-router';
import { Icon } from './Icon';
import { useLongPress } from './useLongPress';
import styles from './Row.module.css';

export interface RowProps {
  title: ReactNode;
  meta?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  /** true = chevron in --ink2; 'accent' = --slate (Project home next step) */
  chevron?: boolean | 'accent';
  /** Larger title (section heading role) */
  large?: boolean;
  to?: string;
  onClick?: () => void;
  /** A shortcut only (DESIGN_SYSTEM §5): whatever it does needs a visible button too. */
  onLongPress?: () => void;
  /** A control of its own at the start (a MiniPad), kept outside the row's button so the two never nest. */
  control?: ReactNode;
  /** A control of its own at the end (a More or Open button), outside the row's button or link. */
  action?: ReactNode;
  /** Select mode: the row toggles, and says whether it's selected. */
  selected?: boolean;
  disabled?: boolean;
}

/** The default content unit: title, meta line, optional trailing value or chevron. */
export function Row({ title, meta, leading, trailing, chevron, large, to, onClick, onLongPress, control, action, selected, disabled }: RowProps) {
  const press = useLongPress(onLongPress, onClick);
  const body = (
    <>
      {leading}
      <span className={styles.text}>
        <span className={large ? styles.titleLarge : styles.title}>{title}</span>
        {meta && <span className={styles.meta}>{meta}</span>}
      </span>
      {(trailing || chevron) && (
        <span className={styles.trailing}>
          {trailing}
          {chevron && (
            <span className={chevron === 'accent' ? styles.accent : undefined}>
              <Icon icon={ChevronRight} />
            </span>
          )}
        </span>
      )}
    </>
  );
  let main: ReactNode;
  if (to && !disabled) main = <Link to={to} className={styles.row}>{body}</Link>;
  else if (control || onClick || disabled) main = <button type="button" className={styles.row} aria-pressed={selected} disabled={disabled} {...press}>{body}</button>;
  else main = <div className={styles.row}>{body}</div>;
  if (control || action) {
    return (
      <div className={styles.split}>
        {control}
        {main}
        {action}
      </div>
    );
  }
  return main;
}

/** A list of rows with hairlines between, above and below. */
export function RowList({ children, label, containerRef }: { children: ReactNode; label?: string; containerRef?: RefObject<HTMLUListElement | null> }) {
  return <ul ref={containerRef} className={styles.list} aria-label={label}>{children}</ul>;
}
